/**
 * VO-QMS-ACOC-001 Rev 1 — Purchase Order & Contract Approval Chain of Custody
 * 
 * Dual-track approval matrix:
 *   Track A — Operational Procurement (vessel spares, consumables, maintenance, technical services)
 *   Track B — Commercial Contracts (charter parties, MSAs, subcontracts, agency agreements)
 * 
 * LLCA Authority Ceilings:
 *   CEO (Jerome Arnold)  — USD 5,000,000  (§5.3a)
 *   COO (Kyle Pemberton) — USD 2,500,000  (§5.3b)
 *   CCO (Bo Jardine)     — USD 1,000,000  (§5.3c)
 */

// ─── Role Mapping ─────────────────────────────────────────────────────────
// Maps employee_permissions.employee_role to ACOC authority roles
export type AcocRole =
  | "vessel_crew"
  | "ops_manager"
  | "tech_supt"
  | "coo"
  | "cco"
  | "ceo"
  | "finance"
  | "dpa_quality"
  | "board";

export const EMPLOYEE_ROLE_TO_ACOC: Record<string, AcocRole> = {
  "Captain": "vessel_crew",
  "Crew": "vessel_crew",
  "Engineer": "tech_supt",
  "Manager": "ops_manager",
  "Shoreside Employee": "ops_manager",
  "Shoreside Leadership": "coo",
  "Executive Team": "ceo",
  "Office": "finance",
  "HSE": "dpa_quality",
};

export const ACOC_ROLE_LABELS: Record<AcocRole, string> = {
  vessel_crew: "Captain / Chief Engineer",
  ops_manager: "Operations Manager",
  tech_supt: "Technical Superintendent",
  coo: "COO",
  cco: "CCO",
  ceo: "CEO",
  finance: "Finance",
  dpa_quality: "DPA / Quality Manager",
  board: "Board of Managers",
};

// ─── Track A — Operational Procurement Tiers ──────────────────────────────
export interface ApprovalTier {
  id: string;
  label: string;
  minValue: number;
  maxValue: number;
  initiator: string;
  approver1: AcocRole;
  approver1Label: string;
  approver2?: AcocRole;
  approver2Label?: string;
  requiresFinance: boolean;
  requiresQES: boolean;
  llcaNote: string;
}

export const TRACK_A_TIERS: ApprovalTier[] = [
  {
    id: "A1", label: "Tier A1", minValue: 0, maxValue: 2500,
    initiator: "Captain / Chief Eng / Ops Mgr",
    approver1: "tech_supt", approver1Label: "Technical Superintendent",
    requiresFinance: false, requiresQES: false,
    llcaNote: "Delegated to Tech. Supt. Single approval.",
  },
  {
    id: "A2", label: "Tier A2", minValue: 2501, maxValue: 25000,
    initiator: "Technical Superintendent",
    approver1: "coo", approver1Label: "COO",
    requiresFinance: false, requiresQES: true,
    llcaNote: "Within COO §5.3(b) sole authority. QES required.",
  },
  {
    id: "A3", label: "Tier A3", minValue: 25001, maxValue: 250000,
    initiator: "Technical Superintendent",
    approver1: "coo", approver1Label: "COO",
    requiresFinance: true, requiresQES: true,
    llcaNote: "Within COO §5.3(b) sole authority. QES + Finance budget check required.",
  },
  {
    id: "A4", label: "Tier A4", minValue: 250001, maxValue: 2500000,
    initiator: "COO",
    approver1: "coo", approver1Label: "COO",
    approver2: "ceo", approver2Label: "CEO (countersign)",
    requiresFinance: true, requiresQES: true,
    llcaNote: "Upper band of COO §5.3(b) ceiling. CEO countersign required.",
  },
  {
    id: "A5", label: "Tier A5", minValue: 2500001, maxValue: 5000000,
    initiator: "COO",
    approver1: "ceo", approver1Label: "CEO",
    approver2: "board", approver2Label: "Board notification",
    requiresFinance: true, requiresQES: true,
    llcaNote: "Exceeds COO §5.3(b) ceiling. CEO sole signatory per §5.3(a). Board notified.",
  },
  {
    id: "A6", label: "Tier A6", minValue: 5000001, maxValue: Infinity,
    initiator: "COO",
    approver1: "ceo", approver1Label: "CEO",
    approver2: "board", approver2Label: "Board (75% Supermajority)",
    requiresFinance: true, requiresQES: true,
    llcaNote: "Exceeds §5.3(a) CEO solo ceiling. Supermajority Board consent required.",
  },
];

// ─── Track B — Commercial Contracts Tiers ─────────────────────────────────
export const TRACK_B_TIERS: ApprovalTier[] = [
  {
    id: "B1", label: "Tier B1", minValue: 0, maxValue: Infinity,
    initiator: "CCO",
    approver1: "cco", approver1Label: "CCO (sole signatory)",
    requiresFinance: false, requiresQES: false,
    llcaNote: "Non-binding instruments (LOI, Term Sheet, MOU, NDA). CCO sole authority.",
  },
  {
    id: "B2", label: "Tier B2", minValue: 0, maxValue: 1000000,
    initiator: "CCO",
    approver1: "cco", approver1Label: "CCO (sole signatory)",
    approver2: "coo", approver2Label: "COO (ops feasibility)",
    requiresFinance: false, requiresQES: true,
    llcaNote: "Within CCO §5.3(c) ceiling. CCO executes; COO confirms ops feasibility.",
  },
  {
    id: "B3", label: "Tier B3", minValue: 1000001, maxValue: 2500000,
    initiator: "CCO",
    approver1: "ceo", approver1Label: "CEO",
    approver2: "coo", approver2Label: "COO + Finance review",
    requiresFinance: true, requiresQES: true,
    llcaNote: "Exceeds CCO ceiling. CEO sole signatory per §5.3(a). Commercial Summary Memo required.",
  },
  {
    id: "B4", label: "Tier B4 (Charter Party)", minValue: 0, maxValue: 5000000,
    initiator: "CCO",
    approver1: "ceo", approver1Label: "CEO",
    approver2: "coo", approver2Label: "COO (vessel/ops terms)",
    requiresFinance: true, requiresQES: true,
    llcaNote: "Charter parties within CEO §5.3(a) ceiling. NOT a §5.6 Reserved Matter.",
  },
  {
    id: "B5", label: "Tier B5", minValue: 2500001, maxValue: 5000000,
    initiator: "CCO",
    approver1: "ceo", approver1Label: "CEO",
    approver2: "board", approver2Label: "Board notification",
    requiresFinance: true, requiresQES: true,
    llcaNote: "Within CEO §5.3(a) ceiling. Commercial Summary Memo + legal review required.",
  },
  {
    id: "B6", label: "Tier B6", minValue: 5000001, maxValue: Infinity,
    initiator: "COO / CCO",
    approver1: "ceo", approver1Label: "CEO + Finance review",
    approver2: "board", approver2Label: "Board (75% Supermajority)",
    requiresFinance: true, requiresQES: true,
    llcaNote: "Exceeds §5.3(a) CEO ceiling. Supermajority or unanimous consent required.",
  },
];

// ─── Tier Determination ───────────────────────────────────────────────────
export function getApprovalTier(
  value: number,
  orderType: "material" | "service",
  scope: "corporate" | "vessel"
): ApprovalTier {
  // Track A for operational (vessel scope or material orders)
  const tiers = TRACK_A_TIERS;
  for (const tier of tiers) {
    if (value >= tier.minValue && value <= tier.maxValue) {
      return tier;
    }
  }
  return TRACK_A_TIERS[TRACK_A_TIERS.length - 1];
}

export function getTrackBTier(value: number, isCharterParty: boolean = false): ApprovalTier {
  if (isCharterParty && value <= 5000000) return TRACK_B_TIERS[3]; // B4
  for (const tier of TRACK_B_TIERS.slice(1)) { // Skip B1 (non-binding)
    if (value >= tier.minValue && value <= tier.maxValue) return tier;
  }
  return TRACK_B_TIERS[TRACK_B_TIERS.length - 1];
}

// ─── Role Authority Check ─────────────────────────────────────────────────
export function canApprove(userAcocRole: AcocRole, tier: ApprovalTier): "approver1" | "approver2" | false {
  // Admin always can approve
  if (userAcocRole === "ceo") {
    if (tier.approver1 === "ceo") return "approver1";
    if (tier.approver2 === "ceo") return "approver2";
    // CEO can approve anything below their ceiling
    return "approver1";
  }
  if (userAcocRole === tier.approver1) return "approver1";
  if (tier.approver2 && userAcocRole === tier.approver2) return "approver2";
  return false;
}

// ─── Approval Chain Display ───────────────────────────────────────────────
export interface ApprovalStep {
  role: AcocRole;
  label: string;
  type: "initiator" | "approver1" | "approver2" | "finance" | "audit";
  status: "pending" | "approved" | "current" | "not_required";
}

export function getApprovalChain(tier: ApprovalTier, currentStatus: string, approvedBy?: string | null): ApprovalStep[] {
  const steps: ApprovalStep[] = [];

  // Approver 1
  const isApproved = currentStatus === "approved" || currentStatus === "ordered" || currentStatus === "delivered" || currentStatus === "completed";
  const isPending = currentStatus === "pending_approval";

  steps.push({
    role: tier.approver1,
    label: tier.approver1Label,
    type: "approver1",
    status: isApproved ? "approved" : isPending ? "current" : "pending",
  });

  // Finance gate
  if (tier.requiresFinance) {
    steps.push({
      role: "finance",
      label: "Finance — Budget Check",
      type: "finance",
      status: isApproved ? "approved" : "pending",
    });
  }

  // Approver 2
  if (tier.approver2) {
    steps.push({
      role: tier.approver2,
      label: tier.approver2Label!,
      type: "approver2",
      status: isApproved ? "approved" : "pending",
    });
  }

  return steps;
}

// ─── Format tier for display ──────────────────────────────────────────────
export function formatValueRange(tier: ApprovalTier): string {
  if (tier.maxValue === Infinity) return `> $${tier.minValue.toLocaleString()}`;
  if (tier.minValue === 0) return `Up to $${tier.maxValue.toLocaleString()}`;
  return `$${tier.minValue.toLocaleString()} – $${tier.maxValue.toLocaleString()}`;
}
